import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useLocation, Navigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Shield, RefreshCw, SkipForward } from 'lucide-react';
import { useState, useEffect } from 'react';
import { PendingApprovalPage } from './PendingApprovalPage';

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useAuth();
  const { canAccessRoute } = usePermissions();
  const location = useLocation();
  const [isRetrying, setIsRetrying] = useState(false);
  const [showSkipOption, setShowSkipOption] = useState(false);
  const [loadingStartTime] = useState(Date.now());
  const [permissionChecked, setPermissionChecked] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        setShowSkipOption(true);
      }
    }, 8000); // Increased from 3 seconds to 8 seconds

    return () => clearTimeout(timer);
  }, [loading]);

  // Only check permissions after user is loaded
  useEffect(() => {
    if (!loading && user) {
      setPermissionChecked(true);
    }
  }, [loading, user]);

  const handleRetry = () => {
    setIsRetrying(true);
    window.location.reload();
  };

  const handleSkip = () => {
    // التحقق من localStorage أولاً
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      window.location.reload();
      return;
    }
    // إعادة توجيه لصفحة تسجيل الدخول
    window.location.href = '/login';
  };

  const getLoadingTime = () => {
    return Math.round((Date.now() - loadingStartTime) / 1000);
  };

  // تحقق من localStorage للمستخدمين التجريبيين
  const currentUser = localStorage.getItem('currentUser');
  if (currentUser && !user && !loading) {
    // يوجد مستخدم في localStorage، اعتبره مسجل دخول
    return <>{children}</>;
  }

  if (loading && getLoadingTime() < 8) { // Increased from 3 to 8 seconds
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">جاري التحميل...</p>
          <p className="text-sm text-gray-500 mb-4">
            وقت التحميل: {getLoadingTime()} ثانية
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">جاري التحميل...</p>
          <p className="text-sm text-gray-500 mb-4">
            وقت التحميل: {getLoadingTime()} ثانية
          </p>
          
          <div className="space-y-3">
            <Button 
              onClick={handleRetry}
              disabled={isRetrying}
              variant="outline"
              className="gap-2 w-full"
            >
              <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
              إعادة تحميل
            </Button>
            
            {showSkipOption && (
              <Button 
                onClick={handleSkip}
                variant="secondary"
                className="gap-2 w-full"
              >
                <SkipForward className="w-4 h-4" />
                الانتقال لصفحة تسجيل الدخول
              </Button>
            )}
          </div>
          
          <p className="text-xs text-gray-400 mt-4">
            إذا استمرت المشكلة، تأكد من اتصال الإنترنت
          </p>
        </div>
      </div>
    );
  }

  // إذا لم يكن المستخدم مسجلاً، انقله لصفحة تسجيل الدخول
  // تطبيق قاعدة إخفاء كل شيء عن الزوار غير المسجلين
  if (!user && !currentUser) {
    return <Navigate to="/login" replace />;
  }

  // التحقق من حالة الموافقة على الموظف
  if (user?.employeeData && user.employeeData.approval_status !== 'approved') {
    return <PendingApprovalPage />;
  }

  // تحقق من صلاحية الوصول للصفحة الحالية - فقط للصفحات غير الأساسية
  const isBasicRoute = location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/employee-profile';
  
  if (permissionChecked && !isBasicRoute && !canAccessRoute(location.pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full p-6">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertDescription className="text-center">
              <div className="space-y-4">
                <h3 className="font-semibold">ليس لديك صلاحية للوصول إلى هذه الصفحة</h3>
                <p className="text-sm">تواصل مع المدير لطلب الصلاحيات المطلوبة</p>
                <div className="text-xs text-gray-500 space-y-1">
                  <p>المسار: {location.pathname}</p>
                  <p>المستخدم: {user?.username || JSON.parse(currentUser || '{}').username}</p>
                </div>
                <Button 
                  onClick={() => window.location.href = '/dashboard'}
                  variant="outline"
                  size="sm"
                >
                  العودة للرئيسية
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}